import { formatDateDDMMYY, formatDateTimeDDMMYY } from '../../utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Card,
  CardContent,
  IconButton,
  Divider,
  Stack,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Tooltip,
  useTheme,
  alpha,
  Alert,
  Avatar,
  Badge,
  LinearProgress,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LoginIcon from '@mui/icons-material/Login';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsIcon from '@mui/icons-material/Settings';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import GavelIcon from '@mui/icons-material/Gavel';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import CodeIcon from '@mui/icons-material/Code';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { VideoPlayer } from '../../components/VideoPlayer';
import { RTMPNetworkAnalyticsCard } from '../../components/RTMPNetworkAnalyticsCard';
import { SRTNetworkAnalyticsCard } from '../../components/SRTNetworkAnalyticsCard';
import {
  getGranularUserDetails,
  updateUserAdmin,
  extendUserTrialAdmin,
  requestUserPasswordChangeAdmin,
  applyManualPayment,
  suspendChannel,
  unsuspendChannel,
  terminateUserAdmin,
  restoreUserAdmin,
  getManualPaymentCatalog,
  setChannelBillingDateAdmin,
  getChannelBillingDateChangesAdmin,
  undoChannelBillingDateChangeAdmin,
  disableAdminUserOutputs,
  setFirstPurchaseDateAdmin,
  clearFirstPurchaseDateAdmin,
  getChannelAdminResources,
  createChannelAdminResource,
  updateChannelAdminResource,
  deleteChannelAdminResource,
  ManualPaymentCatalogResource,
  BillingDateChangeHistory,
  AdminBillingResource,
  AdminBillingResourceConfiguration,
} from '../../services/adminService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getStreamStatsForChannel, getStreamAnalyticsForChannel } from '../../services/streamsService';
import { formatTransactionDescription } from '../../utils/formatTransaction';
import { PLATFORM_PRESETS } from '../../constants/platformPresets';

const knownPlatformNames = new Set(PLATFORM_PRESETS.map((preset) => preset.name.toLowerCase()));

const governanceActionSx = {
  width: 34,
  height: 34,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
} as const;

const formatAdminDestinationLabel = (transcoder: any) => {
  const protocol = String(transcoder.protocol || transcoder.pullProtocol || 'output').toLowerCase();
  const platform = String(transcoder.platformDistributor || '').trim();
  const description = String(transcoder.regionLocation || transcoder.pullLabel || '').trim();
  const normalizedPlatform = platform.toLowerCase();
  const isCustomDestination = !platform
    || normalizedPlatform === 'custom'
    || normalizedPlatform === 'custom destination'
    || (!knownPlatformNames.has(normalizedPlatform) && Boolean(description));
  const target = isCustomDestination
    ? (description || platform || 'Custom Destination')
    : (platform || description || 'Destination');

  return `${protocol} -> ${target}`;
};

type GovernanceChipColor = 'success' | 'warning' | 'error' | 'info' | 'secondary' | 'default';

const outputRuntimeMeta = (output: any): { label: string; color: GovernanceChipColor } => {
  if (output?.isActive === false) return { label: 'Disabled', color: 'default' };
  switch (String(output?.runtimeStatus || '').toLowerCase()) {
    case 'connected':
      return { label: 'Connected', color: 'success' };
    case 'connecting':
      return { label: 'Connecting', color: 'warning' };
    case 'disabled':
      return { label: 'Disabled', color: 'default' };
    default:
      return { label: 'Disconnected', color: 'error' };
  }
};

const channelRuntimeMeta = (channel: any): { label: string; color: GovernanceChipColor } => {
  switch (String(channel?.runtimeStatus || 'OFF').toUpperCase()) {
    case 'LIVE':
      return { label: 'Live', color: 'success' };
    case 'MEDIABOX':
      return { label: 'MediaBox', color: 'info' };
    case 'PLAYKASTER':
      return { label: 'PlayKaster', color: 'secondary' };
    default:
      return { label: 'Off', color: 'default' };
  }
};

type ManualResourceSelection = {
  checked: boolean;
  quantity: number;
  targetChannelId: string;
};

const formatMoney = (currency: string, amount: number) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  } catch (_) {
    return `${currency || 'USD'} ${Number(amount || 0).toFixed(2)}`;
  }
};

const roundChargeAmount = (currency: string, amount: number) => {
  const value = Number(amount || 0);
  const code = String(currency || 'USD').toUpperCase();
  if (['KES', 'UGX', 'TZS'].includes(code)) return Math.ceil(value);
  return Math.ceil(value * 100) / 100;
};

const prorateMultiplier = (billingMode: string, billingCycleEndsAt?: string | null) => {
  if (billingMode !== 'PRORATED' || !billingCycleEndsAt) return 1;
  const end = new Date(billingCycleEndsAt);
  const now = new Date();
  if (Number.isNaN(end.getTime()) || end <= now) return 1;
  const start = new Date(end);
  start.setMonth(start.getMonth() - 1);
  const total = end.getTime() - start.getTime();
  const remaining = end.getTime() - now.getTime();
  if (total <= 0 || remaining <= 0) return 1;
  return Math.max(0, remaining / total);
};

const manualPaymentTypes = [
  'Mpesa Direct',
  'Mpesa Till',
  'Airtel Money',
  'Pesalink',
  'EFT / SWIFT',
  'Digital / Crypto',
];

export const UserDetailAdminView: React.FC = () => {
  const { id } = useParams<{id: string}>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  
  // Modals
  const [previewChannel, setPreviewChannel] = useState<any>(null);
  const [analyticsChannel, setAnalyticsChannel] = useState<any>(null);
  
  // Live Stats
  const [liveStats, setLiveStats] = useState<any>(null);
  const [liveAnalytics, setLiveAnalytics] = useState<any>(null);
  
  // Editable Form Data
  const [formData, setFormData] = useState<any>({});
  const [statusReasonOpen, setStatusReasonOpen] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateMode, setTerminateMode] = useState<'TERMINATE' | 'BAN'>('TERMINATE');
  const [terminateReason, setTerminateReason] = useState('');
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreReason, setRestoreReason] = useState('');
  const [trialExtensionOpen, setTrialExtensionOpen] = useState(false);
  const [trialExtensionDays, setTrialExtensionDays] = useState(1);
  const [trialExtensionReason, setTrialExtensionReason] = useState('');
  const [trialExtensionLoading, setTrialExtensionLoading] = useState(false);
  const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);
  const [passwordChangeReason, setPasswordChangeReason] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [channelActionTarget, setChannelActionTarget] = useState<any>(null);
  const [channelActionReason, setChannelActionReason] = useState('');
  const [channelActionLoading, setChannelActionLoading] = useState(false);
  const [selectedOutputIds, setSelectedOutputIds] = useState<string[]>([]);
  const [outputDisableTarget, setOutputDisableTarget] = useState<{ ids: string[]; label: string } | null>(null);
  const [outputDisableReason, setOutputDisableReason] = useState('');
  const [outputDisableLoading, setOutputDisableLoading] = useState(false);
  const [billingDateTarget, setBillingDateTarget] = useState<any>(null);
  const [billingDateValue, setBillingDateValue] = useState('');
  const [billingDateReason, setBillingDateReason] = useState('');
  const [billingDateProrate, setBillingDateProrate] = useState(true);
  const [billingDateLoading, setBillingDateLoading] = useState(false);
  const [billingDateChanges, setBillingDateChanges] = useState<BillingDateChangeHistory[]>([]);
  const [billingDateHistoryLoading, setBillingDateHistoryLoading] = useState(false);
  const [billingUndoTarget, setBillingUndoTarget] = useState<BillingDateChangeHistory | null>(null);
  const [billingUndoReason, setBillingUndoReason] = useState('');
  const [billingUndoLoading, setBillingUndoLoading] = useState(false);
  const [firstPurchaseOpen, setFirstPurchaseOpen] = useState(false);
  const [firstPurchaseDate, setFirstPurchaseDate] = useState('');
  const [firstPurchaseReason, setFirstPurchaseReason] = useState('');
  const [firstPurchaseLoading, setFirstPurchaseLoading] = useState(false);
  const [resourceChannel, setResourceChannel] = useState<any>(null);
  const [resourceConfig, setResourceConfig] = useState<AdminBillingResourceConfiguration | null>(null);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceEditor, setResourceEditor] = useState<{ mode: 'create' | 'edit' | 'replace'; item?: AdminBillingResource } | null>(null);
  const [resourceForm, setResourceForm] = useState({ productCode: '', quantity: 1, startDate: new Date().toISOString().slice(0, 10), reason: '' });
  const [resourceSelections, setResourceSelections] = useState<string[]>([]);
  const [resourceSaving, setResourceSaving] = useState(false);
  const [resourceDeleteTarget, setResourceDeleteTarget] = useState<AdminBillingResource | null>(null);
  const [resourceDeleteReason, setResourceDeleteReason] = useState('');
  const [resourceDeleteMode, setResourceDeleteMode] = useState<'SCHEDULED' | 'IMMEDIATE'>('SCHEDULED');
  const [resourceDeleteConfirmation, setResourceDeleteConfirmation] = useState('');
  const [manualPaymentOpen, setManualPaymentOpen] = useState(false);
  const [manualCatalog, setManualCatalog] = useState<any>(null);
  const [manualResourceSelections, setManualResourceSelections] = useState<Record<string, ManualResourceSelection>>({});
  const [manualPayment, setManualPayment] = useState<any>({
    amount: '',
    currency: 'USD',
    description: '',
    action: 'RENEW',
    targetPlan: '',
    note: '',
    paymentType: '',
    paymentAccount: '',
    paymentDateTime: '',
    billingMode: 'PRORATED',
  });
  const loadData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [data, catalog] = await Promise.all([
        getGranularUserDetails(id),
        getManualPaymentCatalog(id),
      ]);
      setUser(data);
      setManualCatalog(catalog);
      // Initialize form with ALL editable fields
      setFormData({
        // Profile
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        altPhone: data.altPhone || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        country: data.country || '',
        
        // System Limits
        accountStatus: data.accountStatus,
        role: data.role,
        channelLimit: data.channelLimit,
        transcodeLimit: data.transcodeLimit,
        outputLimit: data.outputLimit
      });
      setManualPayment((prev: any) => ({
        ...prev,
        currency: catalog.currency || data.currency || 'USD',
        targetPlan: data.role,
        targetChannelId: data.channels?.[0]?.id ? String(data.channels[0].id) : '',
        amount: prev.action === 'RENEW' ? String(catalog.renewalAmount || '') : prev.amount,
      }));
    } catch (e) {
      showSnackbar("Failed to fetch user data", "error");
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [id]);

  useEffect(() => {
    if (searchParams.get('edit') !== '1') return;
    setTabIndex(1);
    setEditMode(true);
  }, [searchParams]);

  useEffect(() => {
    if (!analyticsChannel) return;
    const fetchLive = async () => {
      try {
        const [s, a] = await Promise.all([
          getStreamStatsForChannel(analyticsChannel, analyticsChannel.channelHashId || analyticsChannel.hashId),
          getStreamAnalyticsForChannel(analyticsChannel, analyticsChannel.channelHashId || analyticsChannel.hashId)
        ]);
        setLiveStats(s);
        setLiveAnalytics(a);
      } catch (e) { console.error(e); }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 2000);
    return () => clearInterval(interval);
  }, [analyticsChannel]);

  const handleSave = async () => {
    if (formData.accountStatus && user?.accountStatus && formData.accountStatus !== user.accountStatus) {
      setStatusReasonOpen(true);
      return;
    }
    await submitUserUpdate();
  };

  const buildUserUpdatePayload = (reason?: string) => {
    const payload: any = {};
    const stringFields = ['firstName', 'lastName', 'email', 'phone', 'altPhone', 'city', 'postalCode', 'country', 'role', 'accountStatus'];
    const numberFields = ['channelLimit', 'transcodeLimit', 'outputLimit'];

    stringFields.forEach((field) => {
      if (formData[field] !== undefined && String(formData[field] ?? '') !== String(user?.[field] ?? '')) {
        payload[field] = formData[field];
      }
    });
    numberFields.forEach((field) => {
      const next = Number(formData[field] ?? 0);
      const current = Number(user?.[field] ?? 0);
      if (Number.isFinite(next) && next !== current) payload[field] = next;
    });
    if (reason) payload.reason = reason;
    return payload;
  };

  const submitUserUpdate = async (reason?: string) => {
    if (!window.confirm("CONFIRM: Update user profile and limits?")) return;
    try {
      const payload = buildUserUpdatePayload(reason);
      if (Object.keys(payload).length === 0) {
        showSnackbar("No user changes to save", "info");
        return;
      }
      await updateUserAdmin(user.hid || user.id, payload);
      showSnackbar("User Profile Updated", "success");
      setEditMode(false);
      setStatusReasonOpen(false);
      setStatusReason('');
      loadData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || "Update Failed", "error");
    }
  };

  const handleSuspendChannel = async (channel: any) => {
    setChannelActionTarget(channel);
    setChannelActionReason('');
  };

  const submitChannelSuspension = async () => {
    if (!channelActionTarget || !channelActionReason.trim()) return;
    const isSuspended = Boolean(channelActionTarget.billingSuspended);
    setChannelActionLoading(true);
    try {
      if (isSuspended) {
        await unsuspendChannel(channelActionTarget.hid || channelActionTarget.id, channelActionReason.trim());
      } else {
        await suspendChannel(channelActionTarget.hid || channelActionTarget.id, channelActionReason.trim());
      }
      showSnackbar(isSuspended ? 'Channel unsuspended' : 'Channel suspended', 'success');
      setChannelActionTarget(null);
      setChannelActionReason('');
      await loadData();
    } catch(e: any) {
      showSnackbar(e.response?.data?.message || (isSuspended ? 'Failed to unsuspend channel' : 'Failed to suspend channel'), 'error');
    } finally {
      setChannelActionLoading(false);
    }
  };

  const openOutputDisable = (outputIds: string[], label: string) => {
    const ids = Array.from(new Set(outputIds.filter(Boolean)));
    if (ids.length === 0) {
      showSnackbar('No active outputs are selected', 'info');
      return;
    }
    setOutputDisableTarget({ ids, label });
    setOutputDisableReason('');
  };

  const submitOutputDisable = async () => {
    if (!outputDisableTarget || !outputDisableReason.trim()) return;
    setOutputDisableLoading(true);
    try {
      const result = await disableAdminUserOutputs(user.hid || user.id, outputDisableTarget.ids, outputDisableReason.trim());
      showSnackbar(`${result.disabledCount} output${result.disabledCount === 1 ? '' : 's'} disabled`, 'success');
      setOutputDisableTarget(null);
      setOutputDisableReason('');
      setSelectedOutputIds([]);
      await loadData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to disable outputs', 'error');
    } finally {
      setOutputDisableLoading(false);
    }
  };

  const handleTerminateUser = async () => {
    if (!terminateReason.trim()) {
      showSnackbar('Enter a reason', 'error');
      return;
    }
    try {
      await terminateUserAdmin(user.hid || user.id, { mode: terminateMode, reason: terminateReason.trim() });
      showSnackbar(terminateMode === 'BAN' ? 'User banned' : 'User terminated', 'success');
      setTerminateOpen(false);
      setTerminateReason('');
      loadData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'User termination failed', 'error');
    }
  };

  const handleRestoreUser = async () => {
    if (!restoreReason.trim()) {
      showSnackbar('Enter a reason', 'error');
      return;
    }
    try {
      await restoreUserAdmin(user.hid || user.id, restoreReason.trim());
      showSnackbar('User restored', 'success');
      setRestoreOpen(false);
      setRestoreReason('');
      loadData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'User restore failed', 'error');
    }
  };

  const handleExtendTrial = async () => {
    const days = Number(trialExtensionDays);
    if (!Number.isInteger(days) || days < 1 || days > 14) {
      showSnackbar('Trial extension must be between 1 and 14 whole days', 'error');
      return;
    }
    if (!trialExtensionReason.trim()) {
      showSnackbar('Enter a reason for the trial extension', 'error');
      return;
    }

    setTrialExtensionLoading(true);
    try {
      await extendUserTrialAdmin(user.hid || user.id, days, trialExtensionReason.trim());
      showSnackbar(`Trial extended by ${days} day${days === 1 ? '' : 's'}`, 'success');
      setTrialExtensionOpen(false);
      setTrialExtensionDays(1);
      setTrialExtensionReason('');
      await loadData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Trial extension failed', 'error');
    } finally {
      setTrialExtensionLoading(false);
    }
  };

  const handleRequestPasswordChange = async () => {
    if (!passwordChangeReason.trim()) {
      showSnackbar('Enter a reason for the password change request', 'error');
      return;
    }
    setPasswordChangeLoading(true);
    try {
      await requestUserPasswordChangeAdmin(user.hid || user.id, passwordChangeReason.trim());
      showSnackbar('Password change link sent to the user', 'success');
      setPasswordChangeOpen(false);
      setPasswordChangeReason('');
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Password change link could not be sent', 'error');
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const loadBillingDateHistory = async (channel: any) => {
    if (!channel) return;
    setBillingDateHistoryLoading(true);
    try {
      const changes = await getChannelBillingDateChangesAdmin(channel.hid || channel.id);
      setBillingDateChanges(changes);
    } catch (e: any) {
      setBillingDateChanges([]);
      showSnackbar(e.response?.data?.message || 'Billing date history could not be loaded', 'error');
    } finally {
      setBillingDateHistoryLoading(false);
    }
  };
  const openBillingDateDialog = (channel: any) => {
    const tomorrow = new Date();
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    const current = String(channel.currentPeriodEnd || '').slice(0, 10);
    setBillingDateTarget(channel);
    setBillingDateValue(/^\d{4}-\d{2}-\d{2}$/.test(current) ? current : tomorrow.toISOString().slice(0, 10));
    setBillingDateReason('');
    setBillingDateProrate(true);
    setBillingDateChanges([]);
    void loadBillingDateHistory(channel);
  };

  const handleSetBillingDate = async () => {
    if (!billingDateTarget || !billingDateValue || !billingDateReason.trim()) return;
    setBillingDateLoading(true);
    try {
      const result = await setChannelBillingDateAdmin(
        billingDateTarget.hid || billingDateTarget.id,
        billingDateValue,
        billingDateReason.trim(),
        billingDateProrate,
      );
      if (result.pendingPayment) {
        showSnackbar(
          `Proration invoice ${result.invoice?.invoiceNumber || ''} issued for ${formatMoney(result.currency, result.prorationAmountMinor / 100)}. The date changes to ${formatDateDDMMYY(result.billingDate)} only after full payment.`,
          'success',
        );
      } else {
        showSnackbar(`Billing date updated for ${billingDateTarget.name} to ${formatDateDDMMYY(result.billingDate)}.`, 'success');
      }
      setBillingDateTarget(null);
      setBillingDateReason('');
      await loadData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Billing date update failed', 'error');
    } finally {
      setBillingDateLoading(false);
    }
  };

  const openFirstPurchaseDialog = () => {
    setFirstPurchaseDate(String(user?.financialProfile?.firstPurchaseDate || '').slice(0, 10));
    setFirstPurchaseReason('');
    setFirstPurchaseOpen(true);
  };

  const handleSetFirstPurchaseDate = async () => {
    if (!user || !firstPurchaseDate || firstPurchaseReason.trim().length < 3) return;
    setFirstPurchaseLoading(true);
    try {
      await setFirstPurchaseDateAdmin(user.hid || user.id, firstPurchaseDate, firstPurchaseReason.trim());
      showSnackbar('First purchase date updated.', 'success');
      setFirstPurchaseOpen(false);
      await loadData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'First purchase date update failed', 'error');
    } finally {
      setFirstPurchaseLoading(false);
    }
  };

  const handleClearFirstPurchaseDate = async () => {
    if (!user || firstPurchaseReason.trim().length < 3) return;
    setFirstPurchaseLoading(true);
    try {
      await clearFirstPurchaseDateAdmin(user.hid || user.id, firstPurchaseReason.trim());
      showSnackbar('First purchase override cleared.', 'success');
      setFirstPurchaseOpen(false);
      await loadData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'First purchase override could not be cleared', 'error');
    } finally {
      setFirstPurchaseLoading(false);
    }
  };

  const loadChannelResources = async (channel: any) => {
    setResourceChannel(channel);
    setResourceLoading(true);
    try {
      setResourceConfig(await getChannelAdminResources(channel.hid || channel.id));
    } catch (e: any) {
      setResourceConfig(null);
      showSnackbar(e.response?.data?.message || 'Channel services could not be loaded', 'error');
    } finally {
      setResourceLoading(false);
    }
  };

  const openCreateResource = () => {
    setResourceForm({ productCode: '', quantity: 1, startDate: new Date().toISOString().slice(0, 10), reason: '' });
    setResourceSelections([]);
    setResourceEditor({ mode: 'create' });
  };

  const openEditResource = (item: AdminBillingResource) => {
    setResourceForm({ productCode: item.productCode, quantity: item.quantity, startDate: String(item.startDate || '').slice(0, 10), reason: '' });
    setResourceEditor({ mode: 'edit', item });
  };

  const openReplaceResource = (item: AdminBillingResource) => {
    setResourceForm({ productCode: '', quantity: 1, startDate: new Date().toISOString().slice(0, 10), reason: '' });
    setResourceEditor({ mode: 'replace', item });
  };

  const saveResource = async () => {
    if (!resourceChannel || !resourceEditor || resourceForm.reason.trim().length < 3) return;
    if (resourceEditor.mode === 'create' && resourceSelections.length === 0) return;
    if (resourceEditor.mode !== 'create' && (!resourceForm.productCode || !resourceForm.startDate)) return;
    setResourceSaving(true);
    try {
      const payload = { ...resourceForm, quantity: Math.max(1, Number(resourceForm.quantity || 1)), reason: resourceForm.reason.trim() };
      if (resourceEditor.mode !== 'create' && resourceEditor.item) {
        await updateChannelAdminResource(resourceChannel.hid || resourceChannel.id, resourceEditor.item.itemId, payload);
      } else {
        await createChannelAdminResource(resourceChannel.hid || resourceChannel.id, {
          productCodes: resourceSelections,
          reason: resourceForm.reason.trim(),
        });
      }
      showSnackbar(
        resourceEditor.mode === 'replace'
          ? 'Channel service replaced. The new recurring price applies at the next renewal.'
          : resourceEditor.mode === 'edit'
            ? 'Channel service updated.'
          : 'Services enabled. The prorated invoice has been sent and is due within three days.',
        'success',
      );
      setResourceEditor(null);
      await Promise.all([loadChannelResources(resourceChannel), loadData()]);
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Channel service change failed', 'error');
    } finally {
      setResourceSaving(false);
    }
  };

  const deleteResource = async () => {
    if (!resourceChannel || !resourceDeleteTarget || resourceDeleteReason.trim().length < 3) return;
    if (resourceDeleteMode === 'IMMEDIATE' && resourceDeleteConfirmation !== 'DELETE NOW') return;
    setResourceSaving(true);
    try {
      await deleteChannelAdminResource(resourceChannel.hid || resourceChannel.id, resourceDeleteTarget.itemId, {
        reason: resourceDeleteReason.trim(),
        mode: resourceDeleteMode,
        confirmation: resourceDeleteMode === 'IMMEDIATE' ? resourceDeleteConfirmation : undefined,
      });
      showSnackbar(resourceDeleteMode === 'IMMEDIATE' ? 'Channel service removed.' : 'Channel service deletion scheduled.', 'success');
      setResourceDeleteTarget(null);
      setResourceDeleteReason('');
      setResourceDeleteMode('SCHEDULED');
      setResourceDeleteConfirmation('');
      await Promise.all([loadChannelResources(resourceChannel), loadData()]);
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Channel service could not be removed', 'error');
    } finally {
      setResourceSaving(false);
    }
  };

  const handleUndoBillingDateChange = async () => {
    if (!billingDateTarget || !billingUndoTarget || billingUndoReason.trim().length < 3) return;
    setBillingUndoLoading(true);
    try {
      const result = await undoChannelBillingDateChangeAdmin(
        billingDateTarget.hid || billingDateTarget.id,
        billingUndoTarget.changeId,
        billingUndoReason.trim(),
      );
      const cancelled = String(result.change?.status || '').toUpperCase() === 'CANCELLED';
      showSnackbar(
        cancelled
          ? `Pending billing date change cancelled. The effective date remains ${formatDateDDMMYY(result.effectiveBillingDate)}.`
          : `Billing date restored to ${formatDateDDMMYY(result.effectiveBillingDate)}. Financial records were retained.`,
        'success',
      );
      setBillingUndoTarget(null);
      setBillingUndoReason('');
      setBillingDateTarget((current: any) => current ? { ...current, currentPeriodEnd: result.effectiveBillingDate } : current);
      await Promise.all([
        loadBillingDateHistory(billingDateTarget),
        loadData(),
      ]);
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Billing date change could not be undone', 'error');
    } finally {
      setBillingUndoLoading(false);
    }
  };
  const selectedResourceRows = () => (manualCatalog?.resources || [])
    .map((resource: ManualPaymentCatalogResource) => ({
      resource,
      selection: manualResourceSelections[resource.key],
    }))
    .filter(({ selection }: any) => selection?.checked);

  const lineAmount = (resource: ManualPaymentCatalogResource, selection: ManualResourceSelection) => {
    const quantity = resource.singleUnit ? 1 : Math.max(1, Number(selection.quantity || 1));
    const multiplier = prorateMultiplier(manualPayment.billingMode, user?.billingCycleEndsAt);
    const setup = Number(resource.setupPrice || 0) * quantity;
    if (resource.socialPricing) {
      const channel = user?.channels?.find((ch: any) => String(ch.id) === String(selection.targetChannelId));
      const hasExistingSocial = Number(channel?.socialStreamLimit || 0) > 0;
      const monthly = hasExistingSocial
        ? Number(resource.socialPricing.additionalPrice || 0) * quantity
        : Number(resource.socialPricing.initialPrice || 0) + Math.max(0, quantity - 1) * Number(resource.socialPricing.additionalPrice || 0);
      return setup + monthly * multiplier;
    }
    return setup + Number(resource.unitPrice || 0) * quantity * multiplier;
  };

  const manualResourceTotal = () => selectedResourceRows()
    .reduce((sum: number, row: any) => sum + lineAmount(row.resource, row.selection), 0);

  const updateResourceSelection = (resource: ManualPaymentCatalogResource, patch: Partial<ManualResourceSelection>) => {
    setManualResourceSelections((prev) => {
      let next = { ...prev };
      if (resource.key.startsWith('PLAYKASTR_') && patch.checked) {
        next = Object.fromEntries(Object.entries(next).map(([key, value]) => [
          key,
          key.startsWith('PLAYKASTR_') ? { ...value, checked: false } : value,
        ]));
      }
      if (resource.key.startsWith('PACKAGE_') && patch.checked) {
        next = Object.fromEntries(Object.entries(next).map(([key, value]) => [
          key,
          key.startsWith('PACKAGE_') ? { ...value, checked: false } : value,
        ]));
      }
      const current = next[resource.key] || {
        checked: false,
        quantity: 1,
        targetChannelId: resource.requiresChannel && user?.channels?.[0]?.id ? String(user.channels[0].id) : '',
      };
      next[resource.key] = {
        ...current,
        ...patch,
        quantity: resource.singleUnit ? 1 : Math.max(1, Number(patch.quantity ?? current.quantity ?? 1)),
      };
      return next;
    });
  };

  useEffect(() => {
    if (manualPayment.action === 'RENEW') {
      const renewalAmount = Number(manualCatalog?.renewalAmount || 0);
      setManualPayment((prev: any) => (
        String(prev.amount || '') === String(renewalAmount || '') ? prev : { ...prev, amount: renewalAmount ? String(renewalAmount) : '' }
      ));
      return;
    }
    const total = manualResourceTotal();
    setManualPayment((prev: any) => {
      const nextAmount = total ? String(roundChargeAmount(manualCatalog?.currency || prev.currency || user?.currency || 'USD', total)) : '';
      return String(prev.amount || '') === nextAmount ? prev : { ...prev, amount: nextAmount };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPayment.action, manualPayment.billingMode, manualResourceSelections, manualCatalog, user?.billingCycleEndsAt]);

  const handleManualPayment = async () => {
    const amount = Number(manualPayment.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      showSnackbar('Enter a valid payment amount', 'error');
      return;
    }

    try {
      const payload: any = {
        amount,
        currency: manualPayment.currency || user.currency || 'USD',
        description: manualPayment.description,
        action: manualPayment.action,
        targetPlan: manualPayment.targetPlan || user.role,
        renewalTarget: manualPayment.renewalTarget,
        billingMode: manualPayment.billingMode,
        paymentType: manualPayment.paymentType,
        paymentAccount: manualPayment.paymentAccount,
        paymentDateTime: manualPayment.paymentDateTime,
        note: manualPayment.note,
      };

      if (!manualPayment.paymentType) {
        showSnackbar('Select payment type', 'error');
        return;
      }
      if (!String(manualPayment.paymentAccount || '').trim()) {
        showSnackbar('Enter number/account', 'error');
        return;
      }
      if (!manualPayment.paymentDateTime) {
        showSnackbar('Select payment date and time', 'error');
        return;
      }
      if (!String(manualPayment.note || '').trim()) {
        showSnackbar('Enter payment code', 'error');
        return;
      }

      if (manualPayment.action === 'ADD_RESOURCE') {
        const rows = selectedResourceRows();
        if (rows.length === 0) {
          showSnackbar('Select at least one resource', 'error');
          return;
        }
        const missingChannel = rows.find(({ resource, selection }: any) => resource.requiresChannel && !selection.targetChannelId);
        if (missingChannel) {
          showSnackbar(`Select the target channel for ${missingChannel.resource.label}`, 'error');
          return;
        }
        payload.items = rows.map(({ resource, selection }: any) => ({
          resourceType: resource.resourceType,
          quantity: resource.singleUnit ? 1 : Math.max(1, Number(selection.quantity || 1)),
          targetChannelId: resource.requiresChannel && selection.targetChannelId ? Number(selection.targetChannelId) : undefined,
          tierCode: resource.tierCode,
        }));
      }

      await applyManualPayment(user.hid || user.id, payload);
      showSnackbar('Manual payment applied', 'success');
      setManualPaymentOpen(false);
      setManualPayment((prev: any) => ({
        ...prev,
        amount: '',
        description: '',
        note: '',
        paymentType: '',
        paymentAccount: '',
        paymentDateTime: '',
      }));
      setManualResourceSelections({});
      loadData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Manual payment failed', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!user) return null;

  const totalSpent = user.payments?.reduce((acc: number, p: any) => acc + (p.status === 'paid' ? p.amount : 0), 0) || 0;
  const outputIdentifier = (output: any) => String(output?.hid || output?.id || '');
  const allActiveOutputIds = (user.channels || [])
    .flatMap((channel: any) => channel.transcoders || [])
    .filter((output: any) => output.isActive && outputIdentifier(output))
    .map(outputIdentifier);
  const activeChannels = user.channels?.filter((ch: any) => ch.isActive).length || 0;
  const totalChannels = user.channels?.length || 0;
  const manualCurrency = manualCatalog?.currency || manualPayment.currency || user.currency || 'USD';
  const expiredTrial = ['TRIAL', 'PAST_DUE', 'DISABLED', 'SUSPENDED'].includes(user.accountStatus)
    && !user.accountStatusBeforeRestriction
    && !user.setupFeePaid
    && user.trialExpiresAt
    && new Date(user.trialExpiresAt).getTime() <= Date.now();
  const trialCanBeExtended = user.accountStatus === 'TRIAL' || expiredTrial;

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/users')} sx={{ mb: 3, borderRadius: 0, fontWeight: 600 }}>
        Back to Users
      </Button>

      {/* Header Section - FIXED: Using size prop */}
      <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 0, border: `1px solid ${theme.palette.divider}`, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)` }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={user.accountStatus === 'ACTIVE' ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} /> : <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />}
              >
                <Avatar sx={{ width: 80, height: 80, borderRadius: 0, bgcolor: theme.palette.primary.main, fontSize: 32, fontWeight: 700 }}>
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </Avatar>
              </Badge>
              
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>{user.firstName} {user.lastName}</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>{user.email}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={user.role?.toUpperCase()} size="small" sx={{ borderRadius: 0, fontWeight: 600 }} />
                  <Chip label={`HID: ${user.hid}`} size="small" sx={{ borderRadius: 0, fontFamily: 'monospace' }} variant="outlined" />
                  <Tooltip title="Send password change link">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setPasswordChangeOpen(true)}
                      sx={{ ...governanceActionSx, width: 28, height: 24 }}
                      aria-label="Send password change link"
                    >
                      <LockResetIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                  {trialCanBeExtended && (
                    <Tooltip title="Extend trial period">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MoreTimeIcon />}
                        onClick={() => setTrialExtensionOpen(true)}
                        sx={{ minHeight: 24, py: 0, borderRadius: 1, textTransform: 'none', fontWeight: 800 }}
                      >
                        Extend Trial
                      </Button>
                    </Tooltip>
                  )}
                </Stack>
                {trialCanBeExtended && (
                  <Typography sx={{ mt: 1, fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700 }}>
                    Trial {expiredTrial ? 'expired' : 'expires'} {user.trialExpiresAt ? formatDateTimeDDMMYY(user.trialExpiresAt) : 'not set'}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Card elevation={0} sx={{ borderRadius: 0, border: `1px solid ${theme.palette.divider}` }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>CHANNELS</Typography>
                    <Typography variant="h5" fontWeight={700}>{activeChannels}/{totalChannels}</Typography>
                    <LinearProgress variant="determinate" value={(activeChannels / (user.channelLimit || 1)) * 100} sx={{ mt: 1, height: 4, borderRadius: 0 }} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Card elevation={0} sx={{ borderRadius: 0, border: `1px solid ${theme.palette.divider}` }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>LIFETIME SPEND</Typography>
                    <Typography variant="h5" fontWeight={700}>${totalSpent.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">Across all channels</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs Section */}
      <Paper elevation={0} sx={{ borderRadius: 0, border: `1px solid ${theme.palette.divider}` }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, '& .MuiTab-root': { fontWeight: 600, minHeight: 64 } }}
        >
          <Tab icon={<VideoCameraBackIcon />} iconPosition="start" label="Live Channels" />
          <Tab icon={<PersonIcon />} iconPosition="start" label="Identity & Profile" /> 
          <Tab icon={<AttachMoneyIcon />} iconPosition="start" label="Billing" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Limits" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Audit Log" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          
          {/* TAB 0: LIVE CHANNELS */}
          {tabIndex === 0 && (
            <Stack spacing={2}>
              {allActiveOutputIds.length > 0 && (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1.25 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 900 }}>User Output Control</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Select specific active outputs across this user's channels, or disable every active output.</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="outlined" onClick={() => setSelectedOutputIds(allActiveOutputIds)}>Select all active</Button>
                      <Button size="small" variant="text" disabled={selectedOutputIds.length === 0} onClick={() => setSelectedOutputIds([])}>Clear</Button>
                      <Button size="small" color="warning" variant="outlined" disabled={selectedOutputIds.length === 0} onClick={() => openOutputDisable(selectedOutputIds, 'selected outputs')}>
                        Disable selected ({selectedOutputIds.length})
                      </Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => openOutputDisable(allActiveOutputIds, 'all active outputs')}>Disable all</Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}
              {!user.channels || user.channels.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 0 }}>No channels configured.</Alert>
              ) : (
                user.channels.map((ch: any) => (
                  <Card key={ch.id} elevation={0} sx={{ borderRadius: 0, border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent sx={{ p: 3 }}>
                      {/* FIXED: Using size prop */}
                      <Grid container spacing={3} alignItems="flex-start">
                        
                        {/* 1. CHANNEL IDENTITY & EPG STATUS */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Stack spacing={1}>
                            <Typography variant="h6" fontWeight={700}>{ch.name}</Typography>
                            <Typography variant="body2" fontFamily="monospace">KEY: {ch.streamKeyPin}</Typography>
                            
                            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    label={channelRuntimeMeta(ch).label}
                                    color={channelRuntimeMeta(ch).color}
                                    size="small"
                                    sx={{ borderRadius: 1, fontWeight: 700 }}
                                />
                                {ch.billingSuspended && (
                                  <Chip label="SUSPENDED" color="error" size="small" sx={{ borderRadius: 0, fontWeight: 800 }} />
                                )}
                                {String(ch.billingStatus || '').toUpperCase() === 'TRIAL_EXPIRED' && (
                                  <Chip label="TRIAL EXPIRED" color="warning" variant="outlined" size="small" sx={{ borderRadius: 0, fontWeight: 800 }} />
                                )}
                                {!expiredTrial && String(ch.billingStatus || '').toUpperCase() === 'GRACE_PERIOD' && (
                                  <Chip label={`${ch.gracePeriodDays || user.billingPolicy?.gracePeriodDays || 4}-DAY GRACE`} color="warning" size="small" sx={{ borderRadius: 0, fontWeight: 800 }} />
                                )}
                                {!expiredTrial && String(ch.billingStatus || '').toUpperCase() === 'ACTIVE' && (
                                  <Chip label="BILLING ACTIVE" color="success" variant="outlined" size="small" sx={{ borderRadius: 0, fontWeight: 800 }} />
                                )}
                                {!ch.billingDataAvailable && (
                                  <Chip label="BILLING UNAVAILABLE" color="error" variant="outlined" size="small" sx={{ borderRadius: 0, fontWeight: 800 }} />
                                )}
                                
                                {/* NEW: EPG STATUS INDICATOR */}
                                {ch.epgStatus && ch.epgStatus !== 'NONE' && (
                                    <Chip 
                                        icon={ch.epgStatus === 'GRACENOTE' ? <FeaturedPlayListIcon fontSize="small"/> : <CodeIcon fontSize="small"/>}
                                        label={ch.epgStatus === 'GRACENOTE' ? 'EPG: GRACENOTE' : 'EPG: XMLTV'}
                                        color="secondary"
                                        size="small" 
                                        sx={{ borderRadius: 0, fontWeight: 700 }}
                                    />
                                )}
                            </Stack>
                            {ch.billingDataAvailable && (
                              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 800 }}>
                                {['TRIAL', 'TRIAL_EXPIRED'].includes(String(ch.billingStatus || '').toUpperCase()) ? 'Trial ends' : 'Paid through'} {formatDateDDMMYY(ch.currentPeriodEnd)}
                              </Typography>
                            )}
                            {!expiredTrial && String(ch.billingStatus || '').toUpperCase() === 'GRACE_PERIOD' && (
                              <Typography sx={{ fontSize: '0.72rem', color: 'warning.main', fontWeight: 800 }}>
                                Grace period ends {ch.graceEndsAt ? formatDateDDMMYY(ch.graceEndsAt) : 'not available'}
                              </Typography>
                            )}
                            {!ch.billingDataAvailable && (
                              <Typography sx={{ fontSize: '0.72rem', color: 'error.main', fontWeight: 700 }}>
                                {ch.billingDataError || 'Billing engine state is unavailable.'}
                              </Typography>
                            )}
                          </Stack>
                        </Grid>

                        {/* 2. DESTINATIONS & FEATURES */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
                            OUTPUTS ({ch.transcoders?.length || 0})
                          </Typography>
                          
                          <Box
                            sx={{
                              maxHeight: 150,
                              overflowY: 'auto',
                              pr: 0.5,
                              border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                              bgcolor: alpha(theme.palette.action.hover, 0.12),
                              p: 1,
                            }}
                          >
                          <Stack spacing={0.75}>
                            {ch.transcoders?.map((t: any, idx: number) => {
                                // Determine Chip Style based on feature flags
                                const isScte = t.enableScte35;
                                const isHlsSpecial = t.isHlsSpecial;
                                
                                let label = formatAdminDestinationLabel(t);
                                if (isScte) label += " (SCTE-35)";
                                else if (isHlsSpecial) label += " (HLS Pro)";

                                const outputId = outputIdentifier(t);
                                const runtimeMeta = outputRuntimeMeta(t);
                                return (
                                  <Stack key={outputId || idx} direction="row" spacing={0.5} alignItems="center" minWidth={0}>
                                    <Checkbox
                                      size="small"
                                      disabled={!t.isActive || !outputId}
                                      checked={selectedOutputIds.includes(outputId)}
                                      onChange={() => setSelectedOutputIds((current) => current.includes(outputId) ? current.filter((id) => id !== outputId) : [...current, outputId])}
                                      sx={{ p: 0.25 }}
                                    />
                                    <Chip
                                      label={`${label} - ${runtimeMeta.label}`}
                                      icon={isScte ? <AutoAwesomeIcon fontSize="small" /> : undefined}
                                      size="small"
                                      variant="filled"
                                      color={runtimeMeta.color}
                                      sx={{
                                        borderRadius: 1,
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        justifyContent: 'flex-start',
                                        minWidth: 0,
                                        maxWidth: '100%',
                                        '& .MuiChip-label': {
                                          display: 'block',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        },
                                      }}
                                    />
                                  </Stack>
                                );
                            })}
                            {ch.transcoders?.length === 0 && <Typography variant="caption" color="text.disabled">No outputs</Typography>}
                          </Stack>
                          </Box>
                        </Grid>

                        {/* 3. ACTIONS (Existing) */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                            <Tooltip title="Open channel inspection">
                              <IconButton size="small" onClick={() => navigate(`/admin/streams/channels/${ch.hid || ch.id}`)} sx={governanceActionSx}>
                                <ManageSearchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Watch channel">
                              <IconButton size="small" onClick={() => setPreviewChannel(ch)} sx={governanceActionSx}>
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Analytics">
                              <IconButton size="small" onClick={() => setAnalyticsChannel(ch)} sx={governanceActionSx}>
                                <SignalCellularAltIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={ch.billingSuspended ? 'Unsuspend channel' : 'Suspend channel'}>
                              <IconButton
                                size="small"
                                color={ch.billingSuspended ? 'success' : 'error'}
                                onClick={() => handleSuspendChannel(ch)}
                                sx={governanceActionSx}
                              >
                                {ch.billingSuspended ? <SettingsBackupRestoreIcon fontSize="small" /> : <PowerSettingsNewIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            {String(ch.billingStatus || '').toUpperCase() !== 'TRIAL' && (
                              <Tooltip title="Manage channel billing date">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!ch.billingDataAvailable}
                                    onClick={() => openBillingDateDialog(ch)}
                                    sx={governanceActionSx}
                                  >
                                    <CalendarMonthIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {['SUSPENDED', 'CANCELLED', 'DISABLED'].includes(String(user?.accountStatus || '').toUpperCase()) && (
                              <Tooltip title="Restore user">
                                <IconButton size="small" color="success" onClick={() => setRestoreOpen(true)} sx={governanceActionSx}>
                                  <SettingsBackupRestoreIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Terminate or ban user">
                              <IconButton size="small" color="error" onClick={() => setTerminateOpen(true)} sx={governanceActionSx}>
                                <GavelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          )}

          {/* TAB 1: IDENTITY & PROFILE */}
          {tabIndex === 1 && (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={700}>Personal Details</Typography>
                    <FormControlLabel
                        control={<Switch checked={editMode} onChange={(e) => setEditMode(e.target.checked)} />}
                        label={<Stack direction="row" spacing={1}><EditIcon fontSize="small" /><Typography variant="body2" fontWeight={600}>Edit Mode</Typography></Stack>}
                    />
                </Stack>

                {user.riskProfile && (
                  <Paper elevation={0} sx={{ p: 2.25, mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={900}>Account Risk Profile</Typography>
                        <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>Explainable billing and service-lifecycle evidence. Identity, location, and demographic data are not scored.</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={`${user.riskProfile.level} RISK`}
                          color={user.riskProfile.level === 'LOW' ? 'success' : user.riskProfile.level === 'MODERATE' ? 'warning' : 'error'}
                          size="small"
                          sx={{ borderRadius: 1, fontWeight: 900 }}
                        />
                        <Typography sx={{ fontSize: '1.35rem', fontWeight: 950 }}>{user.riskProfile.score}<Typography component="span" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>/100</Typography></Typography>
                      </Stack>
                    </Stack>

                    <Grid container spacing={1.5}>
                      {Object.entries(user.riskProfile.dimensions || {}).map(([key, dimension]: [string, any]) => (
                        <Grid key={key} size={{ xs: 12, sm: 4 }}>
                          <Box sx={{ p: 1.25, bgcolor: alpha(theme.palette.text.primary, 0.025), borderRadius: 1 }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, textTransform: 'capitalize' }}>{key}</Typography>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 900 }}>{dimension.score}/{dimension.max}</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={dimension.max ? Math.min(100, (dimension.score / dimension.max) * 100) : 0} color={dimension.score === 0 ? 'success' : dimension.score / dimension.max >= 0.5 ? 'error' : 'warning'} sx={{ height: 6, borderRadius: 3 }} />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      <Grid size={{ xs: 12, lg: 7 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', mb: 0.75 }}>Evidence and score effect</Typography>
                        <Stack spacing={0.75}>
                          {(user.riskProfile.factors || []).length === 0 ? (
                            <Alert severity="success" sx={{ borderRadius: 1 }}>No adverse billing or service-lifecycle evidence is active.</Alert>
                          ) : user.riskProfile.factors.map((factor: any) => (
                            <Box key={factor.code} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, p: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                              <Box><Typography sx={{ fontSize: '0.76rem', fontWeight: 900 }}>{factor.label}</Typography><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{factor.detail}</Typography></Box>
                              <Chip label={`${factor.points > 0 ? '+' : ''}${factor.points}`} size="small" color={factor.points <= 0 ? 'success' : factor.points >= 15 ? 'error' : 'warning'} variant="outlined" sx={{ borderRadius: 1, fontWeight: 900, flexShrink: 0 }} />
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 12, lg: 5 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', mb: 0.75 }}>Current evidence</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mb: 1.25 }}>
                          {[
                            ['Overdue invoices', user.riskProfile.evidence?.overdueInvoices || 0],
                            ['Open invoices', user.riskProfile.evidence?.openInvoices || 0],
                            ['Failed / 90 days', user.riskProfile.evidence?.failedPayments90Days || 0],
                            ['Billing holds', user.riskProfile.evidence?.billingSuspendedChannels || 0],
                          ].map(([label, value]) => <Box key={String(label)} sx={{ p: 1, bgcolor: alpha(theme.palette.text.primary, 0.025), borderRadius: 1 }}><Typography sx={{ fontSize: '1rem', fontWeight: 950 }}>{value}</Typography><Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{label}</Typography></Box>)}
                        </Box>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>Recommended action</Typography>
                        {(user.riskProfile.recommendations || []).map((recommendation: string) => <Typography key={recommendation} sx={{ fontSize: '0.72rem', mb: 0.5 }}>- {recommendation}</Typography>)}
                        <Typography sx={{ mt: 1, fontSize: '0.62rem', color: 'text.disabled' }}>Evaluated {formatDateTimeDDMMYY(user.riskProfile.evaluatedAt)}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {editMode && <Alert severity="info" sx={{ mb: 3, borderRadius: 0 }}>Editing user metadata directly updates the database.</Alert>}

                {/* FIXED: Using size prop */}
                <Grid container spacing={3}>
                    {/* Column 1: Contact */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 0 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>CONTACT INFO</Typography>
                            <Stack spacing={2} mt={2}>
                                <TextField label="Email Address" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} fullWidth disabled={!editMode} InputProps={{ sx: { borderRadius: 0 } }} />
                                <TextField label="Phone Number" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} fullWidth disabled={!editMode} InputProps={{ sx: { borderRadius: 0 } }} />
                                <TextField label="Alt. Phone" value={formData.altPhone} onChange={(e) => setFormData({...formData, altPhone: e.target.value})} fullWidth disabled={!editMode} InputProps={{ sx: { borderRadius: 0 } }} />
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Column 2: Name & Address */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 0 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>IDENTITY & LOCATION</Typography>
                            <Stack spacing={2} mt={2}>
                                <Stack direction="row" spacing={2}>
                                    <TextField label="First Name" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} fullWidth disabled={!editMode} InputProps={{ sx: { borderRadius: 0 } }} />
                                    <TextField label="Last Name" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} fullWidth disabled={!editMode} InputProps={{ sx: { borderRadius: 0 } }} />
                                </Stack>
                                <TextField label="City" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} fullWidth disabled={!editMode} InputProps={{ sx: { borderRadius: 0 } }} />
                                <Stack direction="row" spacing={2}>
                                    <TextField label="Country" value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} fullWidth disabled={!editMode} InputProps={{ sx: { borderRadius: 0 } }} />
                                    <TextField label="Postal Code" value={formData.postalCode} onChange={(e) => setFormData({...formData, postalCode: e.target.value})} fullWidth disabled={!editMode} InputProps={{ sx: { borderRadius: 0 } }} />
                                </Stack>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Meta Data (Read-only) */}
                    <Grid size={{ xs: 12 }}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.action.disabled, 0.05), borderRadius: 0 }}>
                            <Stack direction="row" spacing={4} justifyContent="center">
                                <Box>
                                    <Typography variant="caption" color="text.secondary">CREATED AT</Typography>
                                    <Typography variant="body2" fontFamily="monospace">{formatDateTimeDDMMYY(user.createdAt)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">LAST UPDATE</Typography>
                                    <Typography variant="body2" fontFamily="monospace">{formatDateTimeDDMMYY(user.updatedAt)}</Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>

                {editMode && (
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" onClick={() => setEditMode(false)} sx={{ borderRadius: 0 }}>Cancel</Button>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={{ borderRadius: 0 }}>Save Profile Changes</Button>
                    </Box>
                )}
            </Box>
          )}

          {/* TAB 2: FINANCIALS */}
          {tabIndex === 2 && (
              <>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell align="right">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {user.payments?.length === 0 ? (
                                        <TableRow><TableCell colSpan={4} align="center">No payment history.</TableCell></TableRow>
                                    ) : (
                                        user.payments?.map((p: any) => (
                                            <TableRow key={p.id} hover>
                                                <TableCell>{formatDateDDMMYY(p.date)}</TableCell>
                                                <TableCell>{formatTransactionDescription(p.description || p.displayDescription)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{p.currency} {p.amount.toLocaleString()}</TableCell>
                                                <TableCell align="right"><Chip label={p.status} color={p.status === 'paid' ? 'success' : 'warning'} size="small" sx={{ borderRadius: 0 }}/></TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                        <Stack spacing={2}>
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 0 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                                    Billing State
                                </Typography>
                                <Stack spacing={1.25}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Account Status</Typography>
                                        <Typography variant="body2" fontWeight={700}>{user.accountStatus}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Currency</Typography>
                                        <Typography variant="body2" fontWeight={700}>{user.currency || 'USD'}</Typography>
                                    </Box>
                                    <Box>
                                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">First Purchase Date</Typography>
                                          <Typography variant="body2" fontWeight={700}>
                                            {user.financialProfile?.firstPurchaseDate ? formatDateDDMMYY(user.financialProfile.firstPurchaseDate) : 'Not available'}
                                          </Typography>
                                          {user.financialProfile?.firstPurchaseOverridden && (
                                            <Typography variant="caption" color="warning.main">Administrative override</Typography>
                                          )}
                                        </Box>
                                        <Tooltip title="Correct first purchase date">
                                          <IconButton size="small" onClick={openFirstPurchaseDialog} sx={governanceActionSx}>
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    </Box>
                                    <Divider />
                                    <Typography variant="caption" color="text.secondary" fontWeight={800}>CHANNEL BILLING DATES</Typography>
                                    {(user.channels || []).map((channel: any) => (
                                      <Box key={channel.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography variant="body2" fontWeight={800} noWrap>{channel.name}</Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {String(channel.billingStatus || 'UNKNOWN').replace(/_/g, ' ')}
                                            {String(channel.billingStatus || '').toUpperCase() === 'GRACE_PERIOD' && channel.graceEndsAt
                                              ? ` / Grace ends ${formatDateDDMMYY(channel.graceEndsAt)}`
                                              : ''}
                                          </Typography>
                                        </Box>
                                        <Typography variant="body2" fontWeight={800} sx={{ whiteSpace: 'nowrap' }}>
                                          {channel.billingDataAvailable ? formatDateDDMMYY(channel.currentPeriodEnd) : 'Unavailable'}
                                        </Typography>
                                      </Box>
                                    ))}
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 0 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                    Manual Payment
                                </Typography>
                                <Stack spacing={1.5}>
                                    <Alert severity="info" sx={{ borderRadius: 0 }}>
                                        Use this when payment was received off-platform and the account or purchased resource should be activated immediately.
                                    </Alert>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Current calculated amount</Typography>
                                            <Typography sx={{ fontWeight: 900 }}>{manualPayment.amount ? formatMoney(manualCurrency, Number(manualPayment.amount)) : 'Select action'}</Typography>
                                        </Box>
                                        <Button variant="contained" startIcon={<AttachMoneyIcon />} onClick={() => setManualPaymentOpen(true)} sx={{ borderRadius: 0 }}>
                                            Open Manual Payment
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ mt: 3, borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle1" fontWeight={900}>Channel Services & Packages</Typography>
                        <Typography variant="caption" color="text.secondary">Go-authoritative service records. New services start immediately and are prorated to the channel billing date.</Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {(user.channels || []).map((channel: any) => (
                          <Button
                            key={channel.id}
                            size="small"
                            variant={resourceChannel?.id === channel.id ? 'contained' : 'outlined'}
                            onClick={() => loadChannelResources(channel)}
                            sx={{ borderRadius: 1 }}
                          >
                            {channel.name}
                          </Button>
                        ))}
                      </Stack>
                    </Stack>
                  </Box>
                  {!resourceChannel ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="text.secondary">Select a channel to manage its services.</Typography></Box>
                  ) : resourceLoading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
                  ) : (
                    <>
                      <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <Typography fontWeight={800}>{resourceChannel.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{resourceConfig?.resources?.length || 0} configured billing items</Typography>
                        </Box>
                        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreateResource} sx={{ borderRadius: 1 }}>Add service</Button>
                      </Box>
                      <TableContainer sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Service</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Quantity</TableCell>
                              <TableCell>Start date</TableCell>
                              <TableCell>Last reason</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(resourceConfig?.resources || []).map((item) => (
                              <TableRow key={item.itemId} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={800}>{item.name || item.productCode}</Typography>
                                  <Typography variant="caption" color="text.secondary">{item.productCode}</Typography>
                                  {item.provisionStatus === 'PENDING_PAYMENT' && (
                                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 700 }}>
                                      {item.invoiceNumber || 'Prorated invoice'} due {formatDateTimeDDMMYY(item.paymentDeadline)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={item.provisionStatus === 'PENDING_PAYMENT' ? 'PAYMENT DUE' : item.status}
                                    color={item.provisionStatus === 'PENDING_PAYMENT' ? 'warning' : item.status === 'ACTIVE' ? 'success' : item.status === 'SCHEDULED' ? 'warning' : 'default'}
                                    sx={{ borderRadius: 1 }}
                                  />
                                </TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell>{formatDateDDMMYY(item.startDate)}</TableCell>
                                <TableCell sx={{ maxWidth: 240 }}><Typography variant="caption" noWrap>{item.reason || 'System synchronized'}</Typography></TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Edit service"><IconButton size="small" onClick={() => openEditResource(item)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                  <Tooltip title={item.provisionStatus === 'PENDING_PAYMENT' ? 'Pay or remove this provisional service before replacing it' : 'Replace service'}>
                                    <span><IconButton size="small" color="primary" disabled={item.provisionStatus === 'PENDING_PAYMENT'} onClick={() => openReplaceResource(item)}><SwapHorizIcon fontSize="small" /></IconButton></span>
                                  </Tooltip>
                                  {!['BASE', 'CHANNEL'].includes(String(item.productCode).toUpperCase()) && (
                                    <Tooltip title="Remove service"><IconButton size="small" color="error" onClick={() => { setResourceDeleteTarget(item); setResourceDeleteReason(''); setResourceDeleteMode(item.provisionStatus === 'PENDING_PAYMENT' ? 'IMMEDIATE' : 'SCHEDULED'); setResourceDeleteConfirmation(''); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                            {(resourceConfig?.resources || []).length === 0 && (
                              <TableRow><TableCell colSpan={6} align="center">No services configured for this channel.</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Paper>
              </>
          )}

          {/* TAB 3: LIMITS */}
          {tabIndex === 3 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>Resource Constraints</Typography>
                <FormControlLabel control={<Switch checked={editMode} onChange={(e) => setEditMode(e.target.checked)} color="warning" />} label={<Stack direction="row" spacing={1}><EditIcon fontSize="small" /><Typography variant="body2" fontWeight={600}>Edit Mode</Typography></Stack>} />
              </Stack>
              {/* FIXED: Using size prop */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField label="Channel Limit" type="number" value={formData.channelLimit || ''} onChange={(e) => setFormData({...formData, channelLimit: parseInt(e.target.value)})} disabled={!editMode} fullWidth InputProps={{ sx: { borderRadius: 0 } }} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField label="Output Limit" type="number" value={formData.outputLimit || ''} onChange={(e) => setFormData({...formData, outputLimit: parseInt(e.target.value)})} disabled={!editMode} fullWidth InputProps={{ sx: { borderRadius: 0 } }} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField label="Transcode Limit" type="number" value={formData.transcodeLimit || ''} onChange={(e) => setFormData({...formData, transcodeLimit: parseInt(e.target.value)})} disabled={!editMode} fullWidth InputProps={{ sx: { borderRadius: 0 } }} />
                </Grid>
              </Grid>
              {editMode && <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={{ mt: 3, borderRadius: 0 }}>Save Limits</Button>}
            </Box>
          )}

          {/* TAB 4: AUDIT LOG */}
          {tabIndex === 4 && (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, border: `1px solid ${theme.palette.divider}` }}>
              <Table>
                <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {user.auditLogs?.map((log: any, idx: number) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{formatDateTimeDDMMYY(log.createdAt)}</TableCell>
                      <TableCell><Chip label={log.action} size="small" sx={{ borderRadius: 0, fontWeight: 600 }} /></TableCell>
                      <TableCell>{log.details}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Modals: Video & Analytics */}
      <Dialog open={!!previewChannel} onClose={() => setPreviewChannel(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>Preview: {previewChannel?.name}</DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'black', display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', aspectRatio: '16/9' }}>
                {previewChannel && (
                    <VideoPlayer 
                        channelId={previewChannel.id}
                        channelHashId={previewChannel.channelHashId || previewChannel.hashId}
                        streamName={previewChannel.name}
                        streamKeyPin={previewChannel.streamKeyPin || ''}
                        isLive={true} 
                        autoPlay={true}
                    />
                )}
            </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={!!analyticsChannel} onClose={() => setAnalyticsChannel(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
  <DialogTitle>Analytics: {analyticsChannel?.name}</DialogTitle>
  <DialogContent sx={{ p: 3 }}>
    {liveStats ? (
      (() => {
        // Determine if stream is online based on bitrate
        const isOnline = liveAnalytics?.bitrateKbps > 0;
        
        return liveStats.type === 'srtConn' ? (
          <SRTNetworkAnalyticsCard 
            stats={liveStats} 
            analytics={liveAnalytics} 
            isOnline={isOnline} 
          />
        ) : (
          <RTMPNetworkAnalyticsCard 
            stats={liveStats} 
            analytics={liveAnalytics} 
            isOnline={isOnline} 
          />
        );
      })()
    ) : (
      <CircularProgress />
    )}
  </DialogContent>
</Dialog>

      <Dialog open={manualPaymentOpen} onClose={() => setManualPaymentOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 900, borderBottom: `1px solid ${theme.palette.divider}` }}>
          Manual Payment
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2.5 }}>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
              Use this when payment was received off-platform and the account or purchased resource should be activated immediately.
            </Alert>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  label="Manual payment type"
                  select
                  size="small"
                  value={manualPayment.action}
                  onChange={(e) => setManualPayment({
                    ...manualPayment,
                    action: e.target.value,
                    renewalTarget: e.target.value === 'RENEW' ? (manualPayment.renewalTarget || 'SUBSCRIPTION') : manualPayment.renewalTarget,
                  })}
                  InputProps={{ sx: { borderRadius: 0 } }}
                  fullWidth
                >
                  <MenuItem value="RENEW">Renew</MenuItem>
                  <MenuItem value="ADD_RESOURCE">Add Resource</MenuItem>
                </TextField>

                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  value={manualPayment.amount}
                  InputProps={{ readOnly: true, sx: { borderRadius: 0 } }}
                  helperText={manualPayment.action === 'RENEW' ? 'One billing cycle from the current first purchase date.' : 'Calculated from selected resources.'}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Receipt description override"
                size="small"
                value={manualPayment.description}
                onChange={(e) => setManualPayment({ ...manualPayment, description: e.target.value })}
                placeholder="Leave as generated unless custom wording is needed"
                InputProps={{ sx: { borderRadius: 0 } }}
              />

              {manualPayment.action === 'ADD_RESOURCE' && (
                <Stack spacing={1.25}>
                  <TextField
                    label="Billing mode"
                    select
                    size="small"
                    value={manualPayment.billingMode}
                    onChange={(e) => setManualPayment({ ...manualPayment, billingMode: e.target.value })}
                    InputProps={{ sx: { borderRadius: 0 } }}
                  >
                    <MenuItem value="PRORATED">Prorated to current billing date</MenuItem>
                    <MenuItem value="NEW_SERVICE_DATE">Full first month, new service date</MenuItem>
                  </TextField>
                  <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                    <Stack spacing={1}>
                      {(manualCatalog?.resources || []).map((resource: ManualPaymentCatalogResource) => {
                        const selection = manualResourceSelections[resource.key] || {
                          checked: false,
                          quantity: 1,
                          targetChannelId: resource.requiresChannel && user.channels?.[0]?.id ? String(user.channels[0].id) : '',
                        };
                        const amount = selection.checked ? roundChargeAmount(manualCurrency, lineAmount(resource, selection)) : 0;
                        return (
                          <Paper key={resource.key} variant="outlined" sx={{ p: 1.25, borderRadius: 0 }}>
                            <Stack spacing={1}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={Boolean(selection.checked)}
                                      onChange={(e) => updateResourceSelection(resource, { checked: e.target.checked })}
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 800 }}>{resource.label}</Typography>
                                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{resource.description}</Typography>
                                    </Box>
                                  }
                                />
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: selection.checked ? 'primary.main' : 'text.secondary' }}>
                                  {formatMoney(manualCurrency, selection.checked ? amount : Number(resource.unitPrice || 0) + Number(resource.setupPrice || 0))}
                                </Typography>
                              </Stack>
                              {selection.checked && (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                  {resource.requiresChannel && (
                                    <TextField
                                      label="Target Channel"
                                      select
                                      size="small"
                                      value={selection.targetChannelId}
                                      onChange={(e) => updateResourceSelection(resource, { targetChannelId: e.target.value })}
                                      InputProps={{ sx: { borderRadius: 0 } }}
                                      fullWidth
                                    >
                                      {user.channels?.map((channel: any) => (
                                        <MenuItem key={channel.id} value={String(channel.id)}>
                                          {channel.name}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}
                                  {!resource.singleUnit && (
                                    <TextField
                                      label={resource.socialPricing ? 'Purchase units' : 'Quantity'}
                                      type="number"
                                      size="small"
                                      value={selection.quantity}
                                      onChange={(e) => updateResourceSelection(resource, { quantity: Number(e.target.value) || 1 })}
                                      InputProps={{ sx: { borderRadius: 0 } }}
                                      sx={{ width: { xs: '100%', sm: 150 } }}
                                    />
                                  )}
                                </Stack>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                </Stack>
              )}

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  label="Payment type"
                  select
                  required
                  size="small"
                  value={manualPayment.paymentType}
                  onChange={(e) => setManualPayment({ ...manualPayment, paymentType: e.target.value })}
                  InputProps={{ sx: { borderRadius: 0 } }}
                  fullWidth
                >
                  {manualPaymentTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Payment Code"
                  required
                  size="small"
                  value={manualPayment.note}
                  onChange={(e) => setManualPayment({ ...manualPayment, note: e.target.value })}
                  InputProps={{ sx: { borderRadius: 0 } }}
                  fullWidth
                />
              </Stack>

              {manualPayment.paymentType && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Number / Account"
                    required
                    size="small"
                    value={manualPayment.paymentAccount}
                    onChange={(e) => setManualPayment({ ...manualPayment, paymentAccount: e.target.value })}
                    InputProps={{ sx: { borderRadius: 0 } }}
                    fullWidth
                  />
                  <TextField
                    label="Payment Date & Time"
                    required
                    type="datetime-local"
                    size="small"
                    value={manualPayment.paymentDateTime}
                    onChange={(e) => setManualPayment({ ...manualPayment, paymentDateTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: { borderRadius: 0 } }}
                    fullWidth
                  />
                </Stack>
              )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ mr: 'auto', fontSize: '0.82rem', fontWeight: 900 }}>
            Total: {manualPayment.amount ? formatMoney(manualCurrency, Number(manualPayment.amount)) : '--'}
          </Typography>
          <Button onClick={() => setManualPaymentOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleManualPayment} sx={{ borderRadius: 0 }}>
            Apply Manual Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusReasonOpen} onClose={() => setStatusReasonOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Reason Required</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="warning" sx={{ borderRadius: 0 }}>
              Changing account status affects login and service access. Record the reason before saving.
            </Alert>
            <TextField
              autoFocus
              multiline
              minRows={3}
              label="Reason"
              value={statusReason}
              onChange={(event) => setStatusReason(event.target.value)}
              fullWidth
              required
              InputProps={{ sx: { borderRadius: 0 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusReasonOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!statusReason.trim()} onClick={() => submitUserUpdate(statusReason.trim())}>
            Save Change
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={passwordChangeOpen}
        onClose={() => !passwordChangeLoading && setPasswordChangeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Send Password Change Link</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              A secure, single-use link will be emailed to {user.email}. Any earlier password reset link will stop working.
            </Alert>
            <TextField
              autoFocus
              label="Administrative reason"
              value={passwordChangeReason}
              onChange={(event) => setPasswordChangeReason(event.target.value)}
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              helperText="Required for the admin audit record. This reason is not included in the email."
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={passwordChangeLoading} onClick={() => setPasswordChangeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={passwordChangeLoading || !passwordChangeReason.trim()}
            onClick={handleRequestPasswordChange}
            startIcon={passwordChangeLoading ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
          >
            {passwordChangeLoading ? 'Sending...' : 'Send Link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={trialExtensionOpen}
        onClose={() => !trialExtensionLoading && setTrialExtensionOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Extend Trial</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 0 }}>
              Current expiry: {user.trialExpiresAt ? formatDateTimeDDMMYY(user.trialExpiresAt) : 'not set'}. Add up to 14 days.
            </Alert>
            <TextField
              autoFocus
              label="Additional days"
              type="number"
              value={trialExtensionDays}
              onChange={(event) => setTrialExtensionDays(Number(event.target.value))}
              inputProps={{ min: 1, max: 14, step: 1 }}
              fullWidth
              required
            />
            <TextField
              label="Reason"
              value={trialExtensionReason}
              onChange={(event) => setTrialExtensionReason(event.target.value)}
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={trialExtensionLoading} onClick={() => setTrialExtensionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={trialExtensionLoading || !trialExtensionReason.trim() || trialExtensionDays < 1 || trialExtensionDays > 14}
            onClick={handleExtendTrial}
          >
            {trialExtensionLoading ? 'Extending...' : 'Extend Trial'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={firstPurchaseOpen} onClose={() => !firstPurchaseLoading && setFirstPurchaseOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Correct First Purchase Date</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="warning" sx={{ borderRadius: 1 }}>
              This changes the financial-history date shown to the client. It does not rewrite account creation or payment records.
            </Alert>
            <TextField
              autoFocus
              label="First purchase date"
              type="date"
              value={firstPurchaseDate}
              onChange={(event) => setFirstPurchaseDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
              fullWidth
              required
            />
            <TextField label="Reason" value={firstPurchaseReason} onChange={(event) => setFirstPurchaseReason(event.target.value)} multiline minRows={3} inputProps={{ maxLength: 500 }} fullWidth required />
          </Stack>
        </DialogContent>
        <DialogActions>
          {user?.financialProfile?.firstPurchaseOverridden && (
            <Button color="warning" disabled={firstPurchaseLoading || firstPurchaseReason.trim().length < 3} onClick={handleClearFirstPurchaseDate}>Clear override</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button disabled={firstPurchaseLoading} onClick={() => setFirstPurchaseOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={firstPurchaseLoading || !firstPurchaseDate || firstPurchaseReason.trim().length < 3} onClick={handleSetFirstPurchaseDate}>
            {firstPurchaseLoading ? 'Saving...' : 'Save correction'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resourceEditor)} onClose={() => !resourceSaving && setResourceEditor(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>{resourceEditor?.mode === 'replace' ? 'Replace Channel Service' : resourceEditor?.mode === 'edit' ? 'Edit Channel Service' : 'Add Channel Service'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              {resourceEditor?.mode === 'create'
                ? "Selected services are enabled immediately. Billing is prorated to this channel's billing date and the invoice is due within three days."
                : resourceEditor?.mode === 'replace'
                  ? `Replace ${resourceEditor.item?.name || resourceEditor.item?.productCode || 'this service'} atomically. The new entitlement starts on the selected date and its recurring price applies at the next renewal; the current paid period is not rebilled.`
                  : 'Changes apply to the existing recurring service. The effective date controls when its entitlement is available.'}
            </Alert>
            {resourceEditor?.mode === 'create' ? (
              <Box
                role="group"
                aria-label="Services and packages"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1,
                }}
              >
                {(resourceConfig?.catalog?.products || []).filter((product) => product.code !== 'CHANNEL').map((product) => {
                  const checked = resourceSelections.includes(product.code);
                  const alreadyConfigured = (resourceConfig?.resources || []).some((item) => item.productCode === product.code);
                  const selectedPackage = (resourceConfig?.catalog?.products || []).find((candidate) => candidate.package && resourceSelections.includes(candidate.code));
                  const disabled = alreadyConfigured || Boolean(product.package && selectedPackage && selectedPackage.code !== product.code);
                  return (
                    <Box
                      key={product.code}
                      sx={{
                        border: '1px solid',
                        borderColor: checked ? 'primary.main' : 'divider',
                        bgcolor: checked ? (theme) => alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.75,
                        minWidth: 0,
                      }}
                    >
                      <FormControlLabel
                        sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                        control={(
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={disabled}
                            onChange={(_, value) => setResourceSelections((current) => (
                              value
                                ? product.package
                                  ? [...current.filter((code) => !(resourceConfig?.catalog?.products || []).some((candidate) => candidate.code === code && candidate.package)), product.code]
                                  : [...current, product.code]
                                : current.filter((code) => code !== product.code)
                            ))}
                          />
                        )}
                        label={(
                          <Box sx={{ pt: 0.25, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{product.name}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {alreadyConfigured ? 'Already configured' : product.package ? 'Package' : 'Service'} | USD {Number(product.monthlyUsd || 0).toFixed(2)}/mo
                            </Typography>
                          </Box>
                        )}
                      />
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <>
                <TextField select label={resourceEditor?.mode === 'replace' ? 'Replacement service or package' : 'Service or package'} value={resourceForm.productCode} disabled={resourceEditor?.mode === 'edit'} onChange={(event) => setResourceForm((current) => ({ ...current, productCode: event.target.value, quantity: 1 }))} fullWidth required>
                  {(resourceConfig?.catalog?.products || []).filter((product) => product.code !== 'CHANNEL').map((product) => {
                    const configuredElsewhere = (resourceConfig?.resources || []).some((item) => item.itemId !== resourceEditor?.item?.itemId && item.productCode === product.code);
                    const anotherPackageExists = (resourceConfig?.resources || []).some((item) => {
                      if (item.itemId === resourceEditor?.item?.itemId) return false;
                      return Boolean((resourceConfig?.catalog?.products || []).find((candidate) => candidate.code === item.productCode)?.package);
                    });
                    const disabled = resourceEditor?.mode === 'replace' && (product.code === resourceEditor.item?.productCode || configuredElsewhere || (product.package && anotherPackageExists));
                    return <MenuItem key={product.code} value={product.code} disabled={disabled}>{product.name} {product.package ? '(Package)' : ''}{product.code === resourceEditor?.item?.productCode ? ' - current' : ''}</MenuItem>;
                  })}
                </TextField>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={resourceForm.quantity}
                      onChange={(event) => setResourceForm((current) => ({ ...current, quantity: Math.max(1, Number(event.target.value || 1)) }))}
                      disabled={(resourceConfig?.catalog?.products || []).find((product) => product.code === resourceForm.productCode)?.quantityMode === 'SINGLE'}
                      inputProps={{ min: 1, max: 1000 }}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 7 }}>
                    <TextField label="Effective start date" type="date" value={resourceForm.startDate} onChange={(event) => setResourceForm((current) => ({ ...current, startDate: event.target.value }))} InputLabelProps={{ shrink: true }} fullWidth required />
                  </Grid>
                </Grid>
              </>
            )}
            <TextField label="Reason" value={resourceForm.reason} onChange={(event) => setResourceForm((current) => ({ ...current, reason: event.target.value }))} multiline minRows={3} inputProps={{ maxLength: 500 }} fullWidth required />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={resourceSaving} onClick={() => setResourceEditor(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={resourceSaving || resourceForm.reason.trim().length < 3 || (resourceEditor?.mode === 'create' ? resourceSelections.length === 0 : (!resourceForm.productCode || !resourceForm.startDate))}
            onClick={saveResource}
          >
            {resourceSaving ? 'Saving...' : resourceEditor?.mode === 'replace' ? 'Replace service' : resourceEditor?.mode === 'edit' ? 'Save changes' : 'Enable and invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resourceDeleteTarget)} onClose={() => !resourceSaving && setResourceDeleteTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Remove Channel Service</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography sx={{ fontWeight: 800 }}>{resourceDeleteTarget?.name || resourceDeleteTarget?.productCode}</Typography>
            {resourceDeleteTarget?.provisionStatus === 'PENDING_PAYMENT' && (
              <Alert severity="warning" sx={{ borderRadius: 1 }}>
                This service belongs to a provisional batch awaiting payment. Deleting it now removes every service in that batch and voids {resourceDeleteTarget.invoiceNumber || 'the linked invoice'}.
              </Alert>
            )}
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={resourceDeleteMode}
              onChange={(_, value) => value && setResourceDeleteMode(value)}
              aria-label="Deletion timing"
            >
              <ToggleButton value="SCHEDULED" disabled={resourceDeleteTarget?.provisionStatus === 'PENDING_PAYMENT'}>End at renewal</ToggleButton>
              <ToggleButton value="IMMEDIATE">Delete now</ToggleButton>
            </ToggleButtonGroup>
            <Alert severity={resourceDeleteMode === 'IMMEDIATE' ? 'error' : 'warning'} sx={{ borderRadius: 1 }}>
              {resourceDeleteMode === 'IMMEDIATE'
                ? resourceDeleteTarget?.provisionStatus === 'PENDING_PAYMENT'
                  ? 'The provisional services are removed immediately and their unpaid prorated invoice is voided.'
                  : 'The entitlement is removed immediately and outputs beyond the new limit are disabled. Existing paid invoices are not refunded or reversed.'
                : `The service remains available through ${formatDateDDMMYY(resourceConfig?.entitlement?.currentPeriodEnd)} and is excluded from the next renewal invoice.`}
            </Alert>
            <TextField label="Reason" value={resourceDeleteReason} onChange={(event) => setResourceDeleteReason(event.target.value)} multiline minRows={3} inputProps={{ maxLength: 500 }} fullWidth required />
            {resourceDeleteMode === 'IMMEDIATE' && (
              <TextField label="Type DELETE NOW to confirm" value={resourceDeleteConfirmation} onChange={(event) => setResourceDeleteConfirmation(event.target.value)} fullWidth required />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={resourceSaving} onClick={() => setResourceDeleteTarget(null)}>Keep service</Button>
          <Button color={resourceDeleteMode === 'IMMEDIATE' ? 'error' : 'warning'} variant="contained" disabled={resourceSaving || resourceDeleteReason.trim().length < 3 || (resourceDeleteMode === 'IMMEDIATE' && resourceDeleteConfirmation !== 'DELETE NOW')} onClick={deleteResource}>
            {resourceSaving ? 'Applying...' : resourceDeleteMode === 'IMMEDIATE' ? 'Delete now' : 'Schedule deletion'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(billingDateTarget)}
        onClose={() => {
          if (!billingDateLoading && !billingUndoLoading) {
            setBillingDateTarget(null);
            setBillingDateChanges([]);
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Manage Channel Billing Date</Typography>
              <Typography variant="caption" color="text.secondary">{billingDateTarget?.name}</Typography>
            </Box>
            <Chip
              size="small"
              icon={<CalendarMonthIcon />}
              label={`Current: ${billingDateTarget?.currentPeriodEnd ? formatDateDDMMYY(billingDateTarget.currentPeriodEnd) : 'Not set'}`}
              variant="outlined"
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={1.5}>
                <Alert severity="info" sx={{ borderRadius: 1 }}>
                  Set a new paid-through date. Prorated extensions take effect only after the linked invoice is paid in full.
                </Alert>
                <TextField
                  autoFocus
                  label="New billing date"
                  type="date"
                  value={billingDateValue}
                  onChange={(event) => setBillingDateValue(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={billingDateProrate ? { min: new Date(Date.now() + 86400000).toISOString().slice(0, 10) } : undefined}
                  fullWidth
                  required
                />
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 800, color: 'text.secondary' }}>
                    Billing treatment
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={billingDateProrate ? 'prorate' : 'no-proration'}
                    onChange={(_event, value) => {
                      if (value) setBillingDateProrate(value === 'prorate');
                    }}
                    aria-label="Billing date proration"
                  >
                    <ToggleButton value="prorate">Prorate extension</ToggleButton>
                    <ToggleButton value="no-proration">Administrative override</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    {billingDateProrate
                      ? 'Issues an itemized invoice and preserves the current date until full payment.'
                      : 'Changes the date immediately without creating a charge.'}
                  </Typography>
                </Box>
                <TextField
                  label="Reason"
                  value={billingDateReason}
                  onChange={(event) => setBillingDateReason(event.target.value)}
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: 500 }}
                  fullWidth
                  required
                />
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 1.25, bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <HistoryIcon fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Change history</Typography>
                      <Typography variant="caption" color="text.secondary">Cancel pending changes or reverse the latest applied change.</Typography>
                    </Box>
                  </Stack>
                </Box>
                <Stack spacing={0} divider={<Divider flexItem />} sx={{ maxHeight: 340, overflowY: 'auto' }}>
                  {billingDateHistoryLoading ? (
                    <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box>
                  ) : billingDateChanges.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">No billing-date changes recorded.</Typography>
                    </Box>
                  ) : billingDateChanges.map((change) => {
                    const status = String(change.status || '').toUpperCase();
                    const hasPending = billingDateChanges.some((entry) => String(entry.status || '').toUpperCase() === 'PENDING_PAYMENT');
                    const currentDate = String(billingDateTarget?.currentPeriodEnd || '').slice(0, 10);
                    const paidMinor = Number(change.invoiceAmountPaidMinor || 0);
                    const canCancel = status === 'PENDING_PAYMENT' && paidMinor === 0;
                    const canReverse = status === 'APPLIED' && !hasPending && currentDate === String(change.billingDate || '').slice(0, 10);
                    const canUndo = canCancel || canReverse;
                    const statusLabel = status === 'PENDING_PAYMENT'
                      ? 'Awaiting payment'
                      : status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
                    const actionHint = paidMinor > 0
                      ? 'Partially paid changes must be resolved or refunded first.'
                      : status === 'APPLIED' && hasPending
                        ? 'Cancel the pending change first.'
                        : status === 'APPLIED' && !canReverse
                          ? 'Only the latest applied change can be reversed.'
                          : canCancel
                            ? 'Cancel pending change'
                            : 'Reverse applied change';
                    return (
                      <Box key={change.changeId} sx={{ p: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              {formatDateDDMMYY(change.previousBillingDate)} to {formatDateDDMMYY(change.billingDate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {formatDateTimeDDMMYY(change.createdAt)} · {change.reason}
                            </Typography>
                            {Number(change.prorationAmountMinor || 0) > 0 && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                {formatMoney(change.currency, Number(change.prorationAmountMinor) / 100)}
                                {change.invoiceNumber ? ` · ${change.invoiceNumber}` : ''}
                                {paidMinor > 0 ? ` · ${formatMoney(change.currency, paidMinor / 100)} paid` : ''}
                              </Typography>
                            )}
                            {change.reversalReason && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Resolution: {change.reversalReason}
                              </Typography>
                            )}
                          </Box>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip
                              size="small"
                              variant="outlined"
                              color={status === 'APPLIED' ? 'success' : status === 'PENDING_PAYMENT' ? 'warning' : 'default'}
                              label={statusLabel}
                            />
                            {(status === 'PENDING_PAYMENT' || status === 'APPLIED') && (
                              <Tooltip title={actionHint}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={canCancel ? 'warning' : 'primary'}
                                    disabled={!canUndo}
                                    onClick={() => {
                                      setBillingUndoTarget(change);
                                      setBillingUndoReason('');
                                    }}
                                    aria-label={actionHint}
                                  >
                                    <SettingsBackupRestoreIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Undo actions preserve invoices, payments, and audit records. They never erase financial history.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button disabled={billingDateLoading} onClick={() => setBillingDateTarget(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={billingDateLoading || !billingDateValue || !billingDateReason.trim() || billingDateValue === String(billingDateTarget?.currentPeriodEnd || '').slice(0, 10)}
            onClick={handleSetBillingDate}
          >
            {billingDateLoading ? 'Updating...' : (billingDateProrate ? 'Issue Proration Invoice' : 'Apply Override')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(billingUndoTarget)}
        onClose={() => !billingUndoLoading && setBillingUndoTarget(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {String(billingUndoTarget?.status || '').toUpperCase() === 'PENDING_PAYMENT'
            ? 'Cancel Pending Billing-Date Change'
            : 'Reverse Applied Billing-Date Change'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity={String(billingUndoTarget?.status || '').toUpperCase() === 'PENDING_PAYMENT' ? 'warning' : 'error'} sx={{ borderRadius: 1 }}>
              {String(billingUndoTarget?.status || '').toUpperCase() === 'PENDING_PAYMENT'
                ? 'The unpaid proration invoice will be voided. The current effective billing date will not change.'
                : 'The previous billing date will be restored. This does not refund or delete the paid invoice or payment record.'}
            </Alert>
            <Typography variant="body2">
              {billingUndoTarget
                ? `${formatDateDDMMYY(billingUndoTarget.previousBillingDate)} to ${formatDateDDMMYY(billingUndoTarget.billingDate)}`
                : ''}
            </Typography>
            <TextField
              autoFocus
              label="Reason"
              value={billingUndoReason}
              onChange={(event) => setBillingUndoReason(event.target.value)}
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              helperText="Required for the audit trail."
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={billingUndoLoading} onClick={() => setBillingUndoTarget(null)}>Keep Change</Button>
          <Button
            variant="contained"
            color={String(billingUndoTarget?.status || '').toUpperCase() === 'PENDING_PAYMENT' ? 'warning' : 'error'}
            disabled={billingUndoLoading || billingUndoReason.trim().length < 3}
            onClick={handleUndoBillingDateChange}
          >
            {billingUndoLoading
              ? 'Applying...'
              : String(billingUndoTarget?.status || '').toUpperCase() === 'PENDING_PAYMENT'
                ? 'Cancel Change'
                : 'Reverse Change'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(outputDisableTarget)} onClose={() => !outputDisableLoading && setOutputDisableTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Disable {outputDisableTarget?.label}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="warning">
              This immediately stops {outputDisableTarget?.ids.length || 0} selected output{outputDisableTarget?.ids.length === 1 ? '' : 's'}. Purchased capacity remains assigned.
            </Alert>
            <TextField
              autoFocus
              label="Administrative reason"
              value={outputDisableReason}
              onChange={(event) => setOutputDisableReason(event.target.value)}
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={outputDisableLoading} onClick={() => setOutputDisableTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={outputDisableLoading || !outputDisableReason.trim()} onClick={submitOutputDisable}>
            {outputDisableLoading ? 'Disabling...' : 'Disable outputs'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={terminateOpen} onClose={() => setTerminateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Terminate Access</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="error" sx={{ borderRadius: 0 }}>
              This blocks user credentials and suspends all channels, inputs, outputs, MediaBox and PlayKaster services.
            </Alert>
            <TextField
              select
              label="Action"
              value={terminateMode}
              onChange={(event) => setTerminateMode(event.target.value as 'TERMINATE' | 'BAN')}
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 0 } }}
            >
              <MenuItem value="TERMINATE">Terminate User - service ended</MenuItem>
              <MenuItem value="BAN">Ban User - abuse / copyright / policy</MenuItem>
            </TextField>
            <TextField
              multiline
              minRows={3}
              label="Reason"
              value={terminateReason}
              onChange={(event) => setTerminateReason(event.target.value)}
              fullWidth
              required
              InputProps={{ sx: { borderRadius: 0 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={!terminateReason.trim()} onClick={handleTerminateUser}>
            {terminateMode === 'BAN' ? 'Ban User' : 'Terminate User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Restore Access</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="success" sx={{ borderRadius: 0 }}>
              This restores the user account to active status and re-enables services suspended by account termination or ban.
            </Alert>
            <TextField
              multiline
              minRows={3}
              label="Restore reason"
              value={restoreReason}
              onChange={(event) => setRestoreReason(event.target.value)}
              fullWidth
              required
              InputProps={{ sx: { borderRadius: 0 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={!restoreReason.trim()} onClick={handleRestoreUser}>
            Restore User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(channelActionTarget)} onClose={() => !channelActionLoading && setChannelActionTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {channelActionTarget?.billingSuspended ? 'Unsuspend Channel' : 'Suspend Channel'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Alert severity={channelActionTarget?.billingSuspended ? 'info' : 'warning'} sx={{ borderRadius: 0 }}>
              {channelActionTarget?.billingSuspended
                ? `Restore ${channelActionTarget?.name || 'this channel'} and its purchased inputs, automation and outputs.`
                : `Suspend only ${channelActionTarget?.name || 'this channel'}. The user account and other channels remain available.`}
            </Alert>
            <TextField
              label="Reason"
              value={channelActionReason}
              onChange={(event) => setChannelActionReason(event.target.value)}
              multiline
              minRows={3}
              required
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={channelActionLoading} onClick={() => setChannelActionTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={channelActionTarget?.billingSuspended ? 'success' : 'error'}
            disabled={channelActionLoading || !channelActionReason.trim()}
            onClick={submitChannelSuspension}
          >
            {channelActionLoading ? 'Applying...' : channelActionTarget?.billingSuspended ? 'Unsuspend Channel' : 'Suspend Channel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
